<template>
  <div class="bg-lifeboost-red-lighter">
    <div class="relative w-full px-5 py-12 mx-auto overflow-hidden max-w-screen-2xl lg:px-14 xl:px-20 2xl:px-12 xl:py-14 2xl:py-20">
      <div class="flex flex-wrap items-center">
        <div class="w-full md:w-1/2 xl:w-5/12 md:pr-10 xl:pr-28 space-y-3.5 lg:space-y-5 pb-7 md:pb-0 text-center">
          <h2 class="text-3xl font-extrabold md:text-4xl xl:text-6xl font-ww-medium" v-html="mainHeading"></h2>
          <div class="text-base text-gray-500 md:text-lg lg:text-2xl font-open-sans" v-html="mainText"></div>
        </div>
        <div class="w-full md:w-1/2 md:ml-auto md:pl-10 font-open-sans">
          <ul class="max-w-xl">
            <li v-for="(item, index) in items" :key="index" class="flex overflow-hidden">
              <div class="relative pb-8 afterbotomBorder">
                <div class="relative z-10 flex items-center justify-center w-16 h-16 p-3 border border-black rounded-full lg:w-20 lg:h-20 lg:p-5 bg-lifeboost-red-lighter">
                  <nuxt-img
                      class="w-full h-full rounded-lg lg:mb-0"
                      :width="38"
                      :height="38"
                      loading="lazy"
                      format="webp"
                      :alt="item.imgAlt"
                      :src="item.imgSrc"
                  />
                </div>
                <svg class="absolute hidden transform -translate-x-1/2 bottom-1 left-1/2" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="14.5px" height="10.5px"> <path fill-rule="evenodd" stroke="rgb(0, 0, 0)" stroke-width="1px" stroke-linecap="butt" stroke-linejoin="miter" fill-opacity="0" fill="rgb(0, 0, 0)" d="M1.378,0.499 L7.000,7.640 L12.622,0.499 "></path> </svg>
              </div>
              <div class="pl-6 pt-2.5 pb-8 text-base md:text-lg lg:text-2xl text-gray-600 space-y-2.5" v-html="item.text"></div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

const props = defineProps({
  mainHeading: {
    type: String,
    default: 'The Truth <br> About Coffee'
  },
  mainText: {
    type: String,
    default: 'Coffee is loaded with antioxidants and has been shown to reduce inflammation, increase longevity, improve athletic performance, and even strengthen DNA. However, <strong>not all coffee is created equal.</strong> Conventional coffee can contain harmful molds, pesticides, and mycotoxins. Lifeboost is different:'
  },
  items: {
    type: Array,
    default: () => [
      {
        imgSrc: 'assets/images/tc-icon-1.png',
        imgAlt: 'image1',
        text: 'We’ve discovered <strong>the cleanest source of coffee</strong> grown high in the mountains of Central America. We never blend our coffee beans with low-quality sources.'
      },
      {
        imgSrc: 'assets/images/tc-icon-2.png',
        imgAlt: 'image2',
        text: 'All Lifeboost coffee is organically grown and <strong>never sprayed with pesticides.</strong>'
      },
      {
        imgSrc: 'assets/images/tc-icon-3.png',
        imgAlt: 'image3',
        text: 'We sun dry and hand wash the coffee to produce the <strong>lowest acidity possible.</strong>'
      },
      {
        imgSrc: 'assets/images/tc-icon-4.png',
        imgAlt: 'image4',
        text: 'We’ve developed a proprietary roasting process to preserve the <strong>delicious flavor</strong> and health properties of our naturally-grown coffee.'
      },
      {
        imgSrc: 'assets/images/tc-icon-5.png',
        imgAlt: 'image5',
        text: 'We <strong>test for over 400 toxins</strong> to make sure it’s as clean as possible.'
      },
      {
        imgSrc: 'assets/images/tc-icon-6.png',
        imgAlt: 'image6',
        text: '<strong>Each day we freshly roast</strong> coffee so you get delicious, healthy coffee delivered directly to your door.'
      }
    ]
  }
});

defineBuilderComponent({
    friendlyName: "Content Section With Svg",
    inputs: [
        {
            name: "mainHeading",
            type: "richText",
            defaultValue: "The Truth <br> About Coffee"
        },
        {
            name: "mainText",
            type: "richText",
            defaultValue: "Coffee is loaded with antioxidants and has been shown to reduce inflammation, increase longevity, improve athletic performance, and even strengthen DNA. However, <strong>not all coffee is created equal.</strong> Conventional coffee can contain harmful molds, pesticides, and mycotoxins. Lifeboost is different:"
        },
        {
            name: "items",
            type: "list",
            defaultValue: [
                {
                    imgSrc: "assets/images/tc-icon-1.png",
                    imgAlt: "image1",
                    text: "We’ve discovered <strong>the cleanest source of coffee</strong> grown high in the mountains of Central America. We never blend our coffee beans with low-quality sources."
                },
                {
                    imgSrc: "assets/images/tc-icon-2.png",
                    imgAlt: "image2",
                    text: "All Lifeboost coffee is organically grown and <strong>never sprayed with pesticides.</strong>"
                },
                {
                    imgSrc: "assets/images/tc-icon-3.png",
                    imgAlt: "image3",
                    text: "We sun dry and hand wash the coffee to produce the <strong>lowest acidity possible.</strong>"
                },
                {
                    imgSrc: "assets/images/tc-icon-4.png",
                    imgAlt: "image4",
                    text: "We’ve developed a proprietary roasting process to preserve the <strong>delicious flavor</strong> and health properties of our naturally-grown coffee."
                },
                {
                    imgSrc: "assets/images/tc-icon-5.png",
                    imgAlt: "image5",
                    text: "We <strong>test for over 400 toxins</strong> to make sure it’s as clean as possible."
                },
                {
                    imgSrc: "assets/images/tc-icon-6.png",
                    imgAlt: "image6",
                    text: "<strong>Each day we freshly roast</strong> coffee so you get delicious, healthy coffee delivered directly to your door."
                }
            ],
            subFields: [
                {
                    name: "imgSrc",
                    type: "file",
                    allowedFileTypes: [
                        "jpeg",
                        "jpg",
                        "png"
                    ],
                    required: true
                },
                {
                    name: "imgAlt",
                    type: "string"
                },
                {
                    name: "text",
                    type: "richText"
                }
            ]
        }
    ]
});
</script>

<style>
li:not(:last-child) svg {
  margin-left: 1px;
  display: block;
}

li:not(:last-child) .afterbotomBorder:after {
  content: "";
  position: absolute;
  top: -0.5rem;
  left: 50%;
  height: 100%;
  border-left-width: 1px;
  --tw-border-opacity: 1;
  border-color: rgba(0,0,0,var(--tw-border-opacity));
}
</style>